import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileText, Search, Clock, Compass, Home, HeartPulse, Globe, Workflow } from "lucide-react";
import { api } from "../lib/api";
import { LENSES, lensBySlug } from "../data/content";
import NewsletterForm from "../components/NewsletterForm";
import DecisionTreeDownload from "../components/DecisionTreeDownload";
import CarpetCardDownload from "../components/CarpetCardDownload";
import Seo from "../components/Seo";

const CAT_ICON = {
  "decision-systems": Compass,
  "home-systems": Home,
  "body-inputs": HeartPulse,
  "beyond-the-home": Globe,
};

const BADGE_COLOR = {
  Foundational: "#00E599",
  "Most Read": "#FFB259",
  "Research Update": "#43C6F0",
  "Quick Win": "#FFB259",
  Beginner: "#00E599",
  Advanced: "#B98CFF",
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const fmtUpdated = (iso) => {
  if (!iso) return "";
  const [y, m] = iso.split("-");
  return `Updated ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
};

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "popular", label: "Most Popular" },
  { key: "updated", label: "Recently Updated" },
  { key: "beginner", label: "Beginner" },
  { key: "advanced", label: "Advanced" },
];

function sortPapers(arr, sort) {
  const a = [...arr];
  const cmp = (x, y, k) => (y[k] || "").localeCompare(x[k] || "");
  if (sort === "popular") a.sort((x, y) => (y.popularity || 0) - (x.popularity || 0));
  else if (sort === "updated") a.sort((x, y) => cmp(x, y, "date_updated"));
  else if (sort === "beginner") a.sort((x, y) => (x.level === "Beginner" ? 0 : 1) - (y.level === "Beginner" ? 0 : 1) || (y.popularity || 0) - (x.popularity || 0));
  else if (sort === "advanced") a.sort((x, y) => (x.level === "Advanced" ? 0 : 1) - (y.level === "Advanced" ? 0 : 1) || (y.popularity || 0) - (x.popularity || 0));
  else a.sort((x, y) => cmp(x, y, "date_published"));
  return a;
}

const Badge = ({ label }) => {
  const c = BADGE_COLOR[label] || "#6b756b";
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border rounded-sm"
      style={{ color: c, borderColor: `${c}66`, backgroundColor: `${c}12` }}>
      {label}
    </span>
  );
};

function GreenpaperCard({ p, i }) {
  const c = lensBySlug(p.category)?.color || "#00E599";
  const badges = [...(p.badges || []), p.level].filter(Boolean);
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.05 }}>
      <Link to={`/greenpapers/${p.slug}`} data-testid={`greenpaper-card-${p.slug}`}
        className="group relative flex flex-col h-full bg-surface hover:bg-surface-elevated p-7 transition-colors overflow-hidden">
        <span className="absolute top-0 left-0 h-[3px] w-full opacity-60 group-hover:opacity-100 transition-opacity" style={{ background: c }} />
        <div className="flex items-center justify-between gap-3">
          <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border" style={{ color: c, borderColor: `${c}66` }}>{lensBySlug(p.category)?.title || p.category}</span>
          {p.subcategory && <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground truncate">{p.subcategory}</span>}
        </div>
        {badges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {badges.map((b) => <Badge key={b} label={b} />)}
          </div>
        )}
        <h3 className="mt-4 font-display text-xl font-medium leading-snug transition-colors group-hover:text-foreground">{p.title}</h3>
        <p className="mt-3 text-sm text-foreground/60 leading-relaxed flex-1">{p.summary}</p>
        <div className="mt-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {p.read_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.read_time}</span>}
          {p.date_updated && <><span className="text-border">·</span><span>{fmtUpdated(p.date_updated)}</span></>}
        </div>
        <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest group-hover:gap-2.5 transition-all" style={{ color: c }}>
          Read Greenpaper <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </Link>
    </motion.div>
  );
}

export default function GreenpapersPage() {
  const { slug } = useParams();
  const activeLens = slug ? lensBySlug(slug) : null;
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    setLoading(true);
    setQuery("");
    api.get("/greenpapers", { params: slug ? { category: slug } : {} })
      .then((r) => setPapers(r.data))
      .catch(() => setPapers([]))
      .finally(() => setLoading(false));
  }, [slug]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    const list = papers.filter((p) => {
      if (!q) return true;
      const hay = [p.title, p.summary, p.subcategory, lensBySlug(p.category)?.title, (p.tags || []).join(" ")].join(" ").toLowerCase();
      return hay.includes(q);
    });
    return sortPapers(list, sort);
  }, [papers, q, sort]);

  const startHere = useMemo(
    () => (!activeLens && !q ? papers.filter((p) => p.start_here) : []),
    [papers, activeLens, q]
  );

  return (
    <div data-testid="greenpapers-page">
      <Seo
        title={activeLens ? `${activeLens.title} Greenpapers` : "Greenpapers"}
        description={activeLens ? activeLens.long : "Research-backed analyses for making better environmental health decisions. Each Greenpaper translates current research into practical decision frameworks."}
        path={activeLens ? `/greenpapers/category/${activeLens.slug}` : "/greenpapers"} />

      {/* HERO */}
      <section className="relative grain border-b border-border overflow-hidden">
        {activeLens ? (
          <>
            <img src={activeLens.image} alt={activeLens.title} className="absolute inset-0 h-full w-full object-cover opacity-20" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to right, #ffffff, #ffffffe6 45%, #ffffff80), linear-gradient(to top, #ffffff, transparent 65%), linear-gradient(to right, ${activeLens.color}14, transparent 50%)` }} />
          </>
        ) : (
          <div className="absolute inset-0 grid-bg" />
        )}
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20">
          {activeLens && <span className="inline-block h-1.5 w-12 mb-5" style={{ background: activeLens.color }} />}
          <p className="mono-label" style={activeLens ? { color: activeLens.color } : {}}>{activeLens ? `Lens ${activeLens.index}` : "Knowledge base"}</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            {activeLens ? activeLens.title : "Greenpapers"}
          </h1>
          {!activeLens && (
            <p className="mt-5 font-display text-xl md:text-2xl font-light text-foreground/85 leading-snug max-w-2xl">
              Research-backed frameworks for reducing cumulative toxic exposure through better decisions, practical
              implementation, and systems thinking.
            </p>
          )}
          <p className="mt-5 max-w-2xl text-base md:text-lg text-foreground/65 leading-relaxed">
            {activeLens
              ? activeLens.long
              : "Each Greenpaper examines how chemical, material, and environmental exposures accumulate over time, explains what actually matters, and translates current research into practical decision frameworks you can apply with confidence."}
          </p>
        </div>
      </section>

      {/* SEARCH + SORT */}
      <div className="border-b border-border bg-surface/20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-5 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="greenpapers-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Greenpapers..."
              className="w-full bg-surface border border-border pl-11 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none transition-colors"
            />
          </div>
          <div className="relative sm:w-56">
            <select
              data-testid="greenpapers-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-full appearance-none bg-surface border border-border pl-4 pr-10 py-3 text-sm text-foreground focus:border-primary focus:outline-none transition-colors font-mono uppercase tracking-widest text-[11px] cursor-pointer"
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key}>Sort: {s.label}</option>
              ))}
            </select>
            <ArrowRight className="absolute right-4 top-1/2 -translate-y-1/2 h-3.5 w-3.5 rotate-90 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      </div>

      {/* CATEGORY FILTERS */}
      <div className="border-b border-border bg-surface/30 sticky top-16 z-30 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 flex gap-1 overflow-x-auto">
          <Link to="/greenpapers" data-testid="filter-all"
            className={`shrink-0 px-4 py-4 font-mono text-[11px] uppercase tracking-widest border-b-2 transition-colors ${!slug ? "border-primary text-primary" : "border-transparent text-foreground/60 hover:text-foreground"}`}>
            All
          </Link>
          {LENSES.map((l) => {
            const Icon = CAT_ICON[l.slug];
            const active = slug === l.slug;
            return (
              <Link key={l.slug} to={`/greenpapers/category/${l.slug}`} data-testid={`filter-${l.slug}`}
                className="shrink-0 px-4 py-4 font-mono text-[11px] uppercase tracking-widest border-b-2 transition-colors flex items-center gap-2"
                style={active ? { borderColor: l.color, color: l.color } : { borderColor: "transparent" }}>
                {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.75} style={active ? { color: l.color } : {}} />}
                {l.index} {l.title}
              </Link>
            );
          })}
        </div>
      </div>

      {/* START HERE */}
      {!loading && startHere.length > 0 && (
        <section data-testid="start-here" className="border-b border-border bg-surface/20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
              <div>
                <p className="mono-label">Start here</p>
                <h2 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">New here? Begin with the foundations.</h2>
              </div>
              <p className="text-sm text-foreground/55 max-w-sm leading-relaxed">
                The core frameworks that make every other Greenpaper easier to understand and apply.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
              {startHere.map((p, i) => <GreenpaperCard key={p.id} p={p} i={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* DECISION TOOLS */}
      {!activeLens && !q && (
        <section data-testid="decision-tools" className="border-b border-border">
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
            <p className="mono-label">Decision tools</p>
            <h2 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">Apply the frameworks with practical tools</h2>
            <div className="mt-8 relative border border-primary/30 bg-primary/5">
              <span aria-hidden className="absolute -top-px -left-px h-3.5 w-3.5 border-t-2 border-l-2 border-primary/60" />
              <span aria-hidden className="absolute -top-px -right-px h-3.5 w-3.5 border-t-2 border-r-2 border-primary/60" />
              <span aria-hidden className="absolute -bottom-px -left-px h-3.5 w-3.5 border-b-2 border-l-2 border-primary/60" />
              <span aria-hidden className="absolute -bottom-px -right-px h-3.5 w-3.5 border-b-2 border-r-2 border-primary/60" />
              <div className="relative p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-primary/40 text-primary bg-base">
                  <Workflow className="h-7 w-7" strokeWidth={1.5} />
                </span>
                <div className="flex-1 min-w-0">
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-primary/40 text-primary rounded-sm">Decision Tool · Foundations</span>
                  <h3 className="mt-3 font-display text-xl font-medium leading-snug">Cumulative Exposure Decision Tree</h3>
                  <p className="mt-2 text-sm text-foreground/65 leading-relaxed max-w-2xl">
                    Not every exposure deserves the same response. Use this calm, engineering-based tool to identify which sources are repeated, close, high-contact, moisture-related, or practical to reduce — across the home, body, workplace, vehicle, and daily life.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
                  <Link to="/resources/cumulative-exposure-decision-tree" data-testid="decision-tool-view"
                    className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-primary-hover transition-colors">
                    Explore the tool <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <DecisionTreeDownload testid="decision-tool-download" label="Download PDF"
                    className="inline-flex items-center justify-center gap-2 border border-primary text-primary px-5 py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors" />
                </div>
              </div>
            </div>

            <div className="mt-6 relative border border-border bg-surface">
              <div className="relative p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-primary/40 text-primary bg-base">
                  <FileText className="h-7 w-7" strokeWidth={1.5} />
                </span>
                <div className="flex-1 min-w-0">
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-primary/40 text-primary rounded-sm">Printable Worksheet · Interior</span>
                  <h3 className="mt-3 font-display text-xl font-medium leading-snug">Carpet: Keep vs. Replace card</h3>
                  <p className="mt-2 text-sm text-foreground/65 leading-relaxed max-w-2xl">
                    A one-page, print-and-tick quick-decision card for evaluating synthetic and other carpet by condition — moisture, odor, staining, cleanability, and padding — rather than by material type. Join the email list and it's yours free.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
                  <Link to="/resources/carpet-keep-vs-replace" data-testid="carpet-card-tool-view"
                    className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-primary-hover transition-colors">
                    Read the guide <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <CarpetCardDownload testid="carpet-card-tool-download" label="Download PDF"
                    className="inline-flex items-center justify-center gap-2 border border-primary text-primary px-5 py-3 font-mono text-[11px] uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors" />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* LIBRARY */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        {!loading && !activeLens && (
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <p className="mono-label">The library</p>
              <h2 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">
                {q ? `Results for "${query.trim()}"` : "Every Greenpaper"}
              </h2>
            </div>
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{filtered.length} {filtered.length === 1 ? "paper" : "papers"}</span>
          </div>
        )}
        {loading ? (
          <p className="font-mono text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <div data-testid="greenpapers-empty" className="border border-dashed border-border p-12 text-center">
            <FileText className="h-8 w-8 text-primary/60 mx-auto" />
            <h3 className="mt-5 font-display text-xl">{q ? "No Greenpapers match your search" : "New Greenpapers are in development"}</h3>
            <p className="mt-3 text-sm text-foreground/55 max-w-md mx-auto leading-relaxed">
              {q
                ? "Try a different keyword, or browse the full library by clearing your search."
                : "New analyses are added as research evolves and patterns become clearer. Subscribe to Greenpapers to be notified the moment they publish."}
            </p>
            {!q && <div className="mt-8 max-w-md mx-auto"><NewsletterForm /></div>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {filtered.map((p, i) => <GreenpaperCard key={p.id} p={p} i={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
